import { useState } from 'react';
import { Stack } from '@mui/material';
import { useDispatch } from 'react-redux';
import { unwrapResult } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';

import { CustomAlert, CustomButton, Text } from '../../../../shared/uiComponents';
import { errorNormalizer } from '../../../../shared/Helpers/functions';
import { parseWallClockTime } from '../../../../shared/Helpers/timezones';
import { Date as DateIcon, Provider, Session, SessionType, Trash, TrashEmpty } from '../../../../shared/Assets/sessionDetailsSVGs';
import { RED, TEXT } from '../../../../shared/Helpers/colors';
import { AdminTypes } from '../../../../components/Menu';
import { useResetCalendarData } from '../../Helpers/hooks';
import '../InfoEvent/index.scss';

import { DispatchProperties, useSelector } from '../../../../redux/store';
import {
	setEditDiscretionary,
	setInfoDiscretionary,
} from '../../../../redux/State/clientSlice/calendarHelperSlice';
import { deleteDiscretionaryHours } from '../../../../redux/State/clientSlice/calendarSlice';
import { getAdminHoursUsage } from '../../../../redux/State/userSlice/userSessionAllocationSlice';

const DeleteContent = ({ onClose }: { onClose: () => void }) => {
	const dispatch = useDispatch<DispatchProperties>();
	const { resetData } = useResetCalendarData();
	const data = useSelector((state) => state.calendarHelper.infoDiscretionaryData);
	const actorId = useSelector((state) => state.account.user.id);
	const month = useSelector((state) => state.calendarHelper.month);
	const year = useSelector((state) => state.calendarHelper.year);

	const deleteHandler = () => {
		if (!data?.id) return;
		dispatch(deleteDiscretionaryHours(data.id))
			.then(unwrapResult)
			.then(() => {
				toast('Admin Hours deleted');
				resetData();
				dispatch(getAdminHoursUsage({ userId: actorId, date: `${year}-${String(month + 1).padStart(2, '0')}-01` }));
			})
			.catch(errorNormalizer);
	};

	return (
		<div className="deleteContainer">
			<div className="description">
				<Text title="Are you sure you want to delete this Admin Hours entry?" type="psm" color={TEXT.TERTIARY} />
			</div>
			<Stack direction="row" justifyContent="flex-end" alignItems="center" gap="12px" flexWrap="wrap" alignSelf="stretch">
				<CustomButton title="Cancel" onClick={onClose} buttonType="secondaryGray" />
				<CustomButton
					title="Yes, Delete"
					onClick={deleteHandler}
					style={{ backgroundColor: RED.BASE }}
				/>
			</Stack>
		</div>
	);
};

const InfoContent = () => {
	const dispatch = useDispatch<DispatchProperties>();
	const [deleteOpen, setDeleteOpen] = useState<boolean>(false);

	const data = useSelector((state) => state.calendarHelper.infoDiscretionaryData);
	const role = useSelector((state) => state.account.role);
	const userId = useSelector((state) => state.account.user.id);

	const isAdmin = role?.section.id === AdminTypes.admin;
	const isOwner = data?.user.id === userId;
	const canManage = isAdmin || isOwner;

	const openEditHandler = () => {
		dispatch(setInfoDiscretionary(false));
		dispatch(setEditDiscretionary(true));
	};

	return data ? (
		<div className="infoContent">
			<div className="row">
				<div className="icon">
					<Provider />
				</div>
				<div className="rowContent">
					<div className="text">
						<Text title="Provider" type="pxs" fontWeight="medium" color={TEXT.TERTIARY} />
						<Text title={data.user.fullName} type="psm" fontWeight="semiBold" />
					</div>
				</div>
			</div>
			<div className="row">
				<div className="icon">
					<SessionType />
				</div>
				<div className="rowContent">
					<div className="text">
						<Text title="Type" type="pxs" fontWeight="medium" color={TEXT.TERTIARY} />
						<Text title="Admin Hours" type="psm" fontWeight="semiBold" />
					</div>
				</div>
			</div>
			<div className="row">
				<div className="icon">
					<DateIcon />
				</div>
				<div className="rowContent">
					<div className="text">
						<Text title="Date" type="pxs" fontWeight="medium" color={TEXT.TERTIARY} />
						<Text title={dayjs(data.date).format('MM/DD/YYYY')} type="psm" fontWeight="semiBold" />
					</div>
				</div>
			</div>
			<div className="row">
				<div className="icon">
					<Session />
				</div>
				<div className="rowContent">
					<div className="text">
						<Text title="Time" type="pxs" fontWeight="medium" color={TEXT.TERTIARY} />
						<Text
							title={`${parseWallClockTime(data.startTime).format('hh:mm A')} - ${parseWallClockTime(data.endTime).format('hh:mm A')}`}
							type="psm"
							fontWeight="semiBold"
						/>
					</div>
				</div>
			</div>
			{data.clientFullName && <Text title={`Client: ${data.clientFullName}`} type="psm" />}
			{data.serviceCodeName && <Text title={`Service code: ${data.serviceCodeName}`} type="psm" />}
			{data.description && (
				<div className="row">
					<div className="rowContent" style={{ paddingLeft: 56 }}>
						<div className="text">
							<Text title="Description" type="pxs" fontWeight="medium" color={TEXT.TERTIARY} />
							<Text title={data.description} type="psm" fontWeight="semiBold" />
						</div>
					</div>
				</div>
			)}
			{data.notes && (
				<div className="row">
					<div className="rowContent" style={{ paddingLeft: 56 }}>
						<div className="text">
							<Text title="Notes" type="pxs" fontWeight="medium" color={TEXT.TERTIARY} />
							<Text title={data.notes} type="psm" />
						</div>
					</div>
				</div>
			)}
			{canManage && (
				<Stack direction="row" justifyContent="flex-end" alignItems="center" gap="12px" flexWrap="wrap" alignSelf="stretch" className="sessionActions">
					<CustomButton
						title="Delete"
						onClick={() => setDeleteOpen(true)}
						buttonType="secondaryGray"
						startIcon={<TrashEmpty color={RED.BASE} />}
						style={{ backgroundColor: RED.QUATERNARY, color: RED.BASE }}
					/>
					<CustomButton title="Edit" onClick={openEditHandler} />
					<CustomAlert
						open={deleteOpen}
						onClose={() => setDeleteOpen(false)}
						title="Attention"
						titleOnTop={false}
						Content={DeleteContent}
						Icon={() => <Trash color={RED.BASE} />}
						iconBackgroundColor={RED.TERTIARY}
					/>
				</Stack>
			)}
		</div>
	) : (
		<Text title="No Information" center className="padding16" type="psm" />
	);
};

const InfoDiscretionaryHours = () => {
	const dispatch = useDispatch<DispatchProperties>();
	const open = useSelector((state) => state.calendarHelper.popups.infoDiscretionary);

	const closeHandler = () => {
		dispatch(setInfoDiscretionary(false));
	};

	return (
		<CustomAlert
			open={open}
			onClose={closeHandler}
			titleOnTop={false}
			title="Admin Hours Details"
			Content={InfoContent}
			Icon={() => <Session />}
		/>
	);
};

export default InfoDiscretionaryHours;
